/**
 * Origin Display Utility
 *
 * Provides consistent origin/source display logic across all tables (Assets, Seeds, Vulnerabilities).
 * Implements the Chariot rollup pattern where native capabilities display as "Chariot"
 * while preserving integration names and manual imports.
 *
 * NSA/CISA Consolidation (Added 2025-12-29):
 * - NSA CSaaS integrations (assets-import + vulns-import) → "NSA CSaaS"
 * - CISA CH integrations (vulnscan-import + webappscan-import) → "CISA CH"
 * - Consolidation applies to both display (table cells) and filters (dropdown options)
 * - Filter selections are expanded back to actual integration IDs for backend queries
 *
 * Pattern Reference: /technology-refactor/Origin-Display-Pattern-Analysis.md
 * Backend Authority: Capability.Integration() method determines native vs third-party
 */

import { AllIntegrations } from '@/hooks/useIntegration';
import type { DropdownOption } from '@/types/dropdown.types';
import { normalizeOriginName } from '@/utils/statisticValidation.util';

/**
 * Extended dropdown option with group support
 */
export type GroupOption = DropdownOption & {
  isGroup?: boolean;
  items?: DropdownOption[];
};

/**
 * NSA CSaaS Integration IDs
 * Both asset and vulnerability imports consolidate to "NSA CSaaS" display name
 */
export const NSA_CSAAS_INTEGRATIONS = [
  'nsa-csaas-assets-import',
  'nsa-csaas-vulns-import',
] as const;

/**
 * CISA Cyber Hygiene Integration IDs
 * Both vulnerability and web app scans consolidate to "CISA CH" display name
 */
export const CISA_CH_INTEGRATIONS = [
  'cisa-ch-vulnscan-import',
  'cisa-ch-webappscan-import',
] as const;

/**
 * Consolidated display names
 */
export const NSA_CSAAS_DISPLAY = 'NSA CSaaS';
export const CISA_CH_DISPLAY = 'CISA CH';

/**
 * Virtual filter values for consolidated options
 * These are used in filter dropdowns and expanded to actual integration IDs
 */
export const NSA_CSAAS_FILTER_VALUE = 'nsa-csaas';
export const CISA_CH_FILTER_VALUE = 'cisa-ch';

/**
 * Gets display text for multiple origins (used by Assets table).
 * Applies rollup logic to each origin and returns deduplicated array.
 *
 * @param rawOrigins - Array of raw origin values
 * @param sourceFilterOptions - Filter options from useGetVulnerabilityFilters
 * @returns Array of display-ready origin strings
 *
 * @example
 * ```typescript
 * const origins = ['subdomain-enumerator', 'portscanner', 'github'];
 * getDisplayOriginTexts(origins, sourceFilterOptions)
 * // → ["Chariot", "GitHub"] (deduplicated - both native capabilities rolled up)
 * ```
 */
export const getDisplayOriginTexts = (
  rawOrigins: (string | undefined | null)[],
  sourceFilterOptions: GroupOption[]
): string[] => {
  // Filter out null/undefined values
  const validOrigins = rawOrigins.filter(
    (origin): origin is string => !!origin
  );

  // Apply rollup logic to each origin
  const displayOrigins = validOrigins.map(origin =>
    getDisplayOriginText(origin, sourceFilterOptions)
  );

  // Deduplicate using Set
  let result = Array.from(new Set(displayOrigins));

  // If "Manual" is present, exclude "Praetorian" (redundant)
  if (result.includes('Manual')) {
    result = result.filter(origin => origin !== 'Praetorian');
  }

  // Sort so that "Praetorian" is always last
  result.sort((a, b) => {
    if (a === 'Praetorian') return 1;
    if (b === 'Praetorian') return -1;
    return a.localeCompare(b);
  });

  return result;
};

/**
 * Gets display text for origin/source values using Chariot rollup pattern.
 *
 * Display Priority:
 * 1. Manual imports (emails ending in @praetorian.com) → "Manual"
 * 2. Chariot native capabilities (from Chariot group) → "Chariot"
 * 3. Third-party integrations (github, aws, qualys, etc.) → Formatted integration name
 * 4. Unknown sources → "Chariot" (fallback)
 *
 * @param rawOrigin - Raw origin/source value from backend (e.g., "subdomain-enumerator", "github", "user@praetorian.com")
 * @param sourceFilterOptions - Filter options from useGetVulnerabilityFilters containing Chariot group
 * @returns Human-readable display text
 *
 * @example
 * ```typescript
 * // Chariot native capabilities
 * getDisplayOriginText('subdomain-enumerator', sourceFilterOptions) // → "Chariot"
 * getDisplayOriginText('portscanner', sourceFilterOptions) // → "Chariot"
 * getDisplayOriginText('crawler', sourceFilterOptions) // → "Chariot"
 *
 * // Third-party integrations
 * getDisplayOriginText('github', sourceFilterOptions) // → "GitHub"
 * getDisplayOriginText('amazon', sourceFilterOptions) // → "AWS"
 * getDisplayOriginText('qualys', sourceFilterOptions) // → "Qualys"
 *
 * // Manual imports
 * getDisplayOriginText('user@praetorian.com', sourceFilterOptions) // → "Manual"
 *
 * // Special Chariot sources
 * getDisplayOriginText('provided', sourceFilterOptions) // → "Chariot"
 * getDisplayOriginText('adhoc', sourceFilterOptions) // → "Chariot"
 * ```
 */
export const getDisplayOriginText = (
  rawOrigin: string | undefined | null,
  sourceFilterOptions: GroupOption[]
): string => {
  // Handle empty/null values
  if (!rawOrigin) {
    return 'Praetorian';
  }

  const lowerOrigin = rawOrigin.toLowerCase();

  // Priority 1: Manual user imports
  // Any email ending in @praetorian.com is a manual entry
  const isManual = rawOrigin.endsWith('@praetorian.com');
  if (isManual) {
    return 'Manual';
  }

  // Priority 2: Praetorian native capabilities
  // Extract Praetorian group from filter options and check membership
  const chariotGroup = sourceFilterOptions.find(
    sf => sf.isGroup && sf.label === 'Praetorian'
  );
  const chariotSourceValues =
    chariotGroup?.items?.map(item => String(item.value).toLowerCase()) || [];

  const isChariotGroupedSource = chariotSourceValues.includes(lowerOrigin);
  if (isChariotGroupedSource) {
    return 'Praetorian';
  }

  // Priority 2.5: NSA/CISA consolidation (NEW)
  const consolidatedName = getConsolidatedDisplayName(rawOrigin);
  if (consolidatedName) {
    return consolidatedName;
  }

  // Priority 3: Third-party integrations
  // Check if source is a known integration
  const isIntegration = Object.keys(AllIntegrations).includes(lowerOrigin);
  if (isIntegration) {
    return getFormattedLabel(rawOrigin, sourceFilterOptions);
  }

  // Fallback: Default to "Praetorian" for unknown sources
  // This handles edge cases like legacy data or special system sources
  return 'Praetorian';
};

/**
 * Gets formatted label for a source value from filter options.
 * Falls back to normalizeOriginName if not found in filter options.
 *
 * @param sourceValue - Raw source value
 * @param sourceFilterOptions - Filter options containing label mappings
 * @returns Formatted label for display
 */
const getFormattedLabel = (
  sourceValue: string,
  sourceFilterOptions: GroupOption[]
): string => {
  let foundLabel: string | undefined;

  // First check if it's in the Praetorian group items
  const chariotGroupFromFilters = sourceFilterOptions.find(
    sf => sf.isGroup && sf.label === 'Praetorian'
  );
  if (chariotGroupFromFilters?.items) {
    foundLabel = chariotGroupFromFilters.items.find(
      item => String(item.value).toLowerCase() === sourceValue.toLowerCase()
    )?.label;
  }

  // If not found, check top-level integration options
  if (!foundLabel) {
    const integrationOption = sourceFilterOptions.find(
      option =>
        !option.isGroup &&
        String(option.value).toLowerCase() === sourceValue.toLowerCase()
    );
    foundLabel = integrationOption?.label;
  }

  // Fallback to normalizeOriginName for formatting
  return foundLabel || normalizeOriginName(sourceValue);
};

/**
 * Gets consolidated display name for NSA/CISA integrations.
 * Returns null for integrations that don't need consolidation.
 *
 * @param integrationId - Integration ID to check
 * @returns Consolidated display name or null
 *
 * @example
 * getConsolidatedDisplayName('nsa-csaas-assets-import') // → "NSA CSaaS"
 * getConsolidatedDisplayName('github') // → null
 */
export const getConsolidatedDisplayName = (
  integrationId: string | undefined | null
): string | null => {
  if (!integrationId) {
    return null;
  }

  const lowerIntegrationId = integrationId.toLowerCase();

  // Check NSA CSaaS integrations
  if (
    NSA_CSAAS_INTEGRATIONS.map(id => id.toLowerCase()).includes(
      lowerIntegrationId
    )
  ) {
    return NSA_CSAAS_DISPLAY;
  }

  // Check CISA CH integrations
  if (
    CISA_CH_INTEGRATIONS.map(id => id.toLowerCase()).includes(
      lowerIntegrationId
    )
  ) {
    return CISA_CH_DISPLAY;
  }

  return null;
};

/**
 * Collapses expanded integration IDs back to consolidated filter values.
 * Used when displaying selected filters in dropdowns - shows consolidated
 * names instead of individual integration IDs.
 *
 * @param originValues - Array of origin filter values (possibly expanded)
 * @returns Array with consolidated values replacing expanded IDs
 *
 * @example
 * collapseOriginValues(['nsa-csaas-assets-import', 'nsa-csaas-vulns-import', 'github'])
 * // → ['nsa-csaas', 'github']
 *
 * collapseOriginValues(['github', 'aws'])
 * // → ['github', 'aws'] (no consolidation needed)
 */
export const collapseOriginValues = (originValues: string[]): string[] => {
  const result = new Set<string>();
  const processedIds = new Set<string>();

  for (const value of originValues) {
    const lowerValue = value.toLowerCase();

    // Skip if already processed as part of a consolidated group
    if (processedIds.has(lowerValue)) {
      continue;
    }

    // Check if this is part of NSA CSaaS group
    const nsaIds = NSA_CSAAS_INTEGRATIONS.map(id => id.toLowerCase());
    if (nsaIds.includes(lowerValue)) {
      result.add(NSA_CSAAS_FILTER_VALUE);
      // Mark all NSA IDs as processed
      nsaIds.forEach(id => processedIds.add(id));
      continue;
    }

    // Check if this is part of CISA CH group
    const cisaIds = CISA_CH_INTEGRATIONS.map(id => id.toLowerCase());
    if (cisaIds.includes(lowerValue)) {
      result.add(CISA_CH_FILTER_VALUE);
      // Mark all CISA IDs as processed
      cisaIds.forEach(id => processedIds.add(id));
      continue;
    }

    // Not part of a consolidated group - add as-is
    result.add(value);
    processedIds.add(lowerValue);
  }

  return Array.from(result);
};

/**
 * Expands consolidated filter value to actual integration IDs.
 * Used when applying filters - consolidated values need to be expanded
 * to query for all underlying integrations.
 *
 * @param consolidatedValue - The virtual filter value ("nsa-csaas" or "cisa-ch")
 * @returns Array of integration IDs or null if not a consolidated value
 *
 * @example
 * getConsolidatedOriginIds('nsa-csaas')
 * // → ['nsa-csaas-assets-import', 'nsa-csaas-vulns-import']
 *
 * getConsolidatedOriginIds('github') // → null (not consolidated)
 */
export const getConsolidatedOriginIds = (
  consolidatedValue: string | undefined | null
): string[] | null => {
  if (!consolidatedValue) {
    return null;
  }

  const lowerValue = consolidatedValue.toLowerCase();

  if (lowerValue === NSA_CSAAS_FILTER_VALUE) {
    return [...NSA_CSAAS_INTEGRATIONS];
  }

  if (lowerValue === CISA_CH_FILTER_VALUE) {
    return [...CISA_CH_INTEGRATIONS];
  }

  return null;
};

/**
 * Transforms source filter options to consolidate NSA/CISA integrations.
 *
 * Removes individual NSA/CISA integration options and replaces them with
 * consolidated options that users can select in filter dropdowns.
 *
 * @param options - Raw source filter options from useGetVulnerabilityFilters
 * @returns Transformed options with consolidated NSA/CISA entries
 *
 * @example
 * const raw = [
 *   { label: 'NSA CSaaS Attack Surface Management (Assets)', value: 'nsa-csaas-assets-import' },
 *   { label: 'NSA CSaaS Attack Surface Management (Vulnerabilities)', value: 'nsa-csaas-vulns-import' },
 *   { label: 'GitHub', value: 'github' },
 * ];
 * const transformed = transformSourceFilterOptions(raw);
 * // → [{ label: 'NSA CSaaS', value: 'nsa-csaas' }, { label: 'GitHub', value: 'github' }]
 */
export const transformSourceFilterOptions = (
  options: (DropdownOption | GroupOption)[]
): (DropdownOption | GroupOption)[] => {
  const result: (DropdownOption | GroupOption)[] = [];
  let hasNSA = false;
  let hasCISA = false;

  // First pass: identify what needs consolidation and preserve non-consolidated options
  for (const option of options) {
    // Preserve group options (Chariot group)
    if ('isGroup' in option && option.isGroup) {
      result.push(option);
      continue;
    }

    const value = String(option.value || '').toLowerCase();

    // Check if this is an NSA integration
    if (NSA_CSAAS_INTEGRATIONS.map(id => id.toLowerCase()).includes(value)) {
      hasNSA = true;
      continue; // Skip individual NSA options
    }

    // Check if this is a CISA integration
    if (CISA_CH_INTEGRATIONS.map(id => id.toLowerCase()).includes(value)) {
      hasCISA = true;
      continue; // Skip individual CISA options
    }

    // Preserve all other options
    result.push(option);
  }

  // Second pass: add consolidated options if needed
  if (hasNSA) {
    result.push({
      label: NSA_CSAAS_DISPLAY,
      value: NSA_CSAAS_FILTER_VALUE,
      id: NSA_CSAAS_FILTER_VALUE,
    });
  }

  if (hasCISA) {
    result.push({
      label: CISA_CH_DISPLAY,
      value: CISA_CH_FILTER_VALUE,
      id: CISA_CH_FILTER_VALUE,
    });
  }

  // Sort alphabetically (preserving groups at top)
  const groups = result.filter(opt => 'isGroup' in opt && opt.isGroup);
  const nonGroups = result.filter(opt => !('isGroup' in opt && opt.isGroup));
  nonGroups.sort((a, b) => (a.label || '').localeCompare(b.label || ''));

  return [...groups, ...nonGroups];
};
